import { apiGet, apiPost } from '@/lib/api';
import { operationsClient } from '@/lib/api/client';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { useApiQuery } from '../useApiQuery';
import { membershipPlanKeys } from '@/lib/queryKeys';
import { useHasPermission } from '@/hooks/usePermissions';
import { useQueries } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  MembershipPlan,
  MembershipPlanListResponse,
  PlanStatus,
} from '@/components/organisms/pricebook/support/membership-plan-shared';

interface UseMembershipPlansParams {
  search?: string;
  status?: PlanStatus;
  page?: number;
  limit?: number;
}

interface UseMembershipPlansOptions {
  enabled?: boolean;
  showErrorToast?: boolean;
  requiredPermission?: string;
  /** Fetch every page, for catalog-wide decisions such as automatic offers. */
  allPages?: boolean;
}

export function useMembershipPlans(
  params: UseMembershipPlansParams = {},
  options?: UseMembershipPlansOptions,
) {
  const { search, status, page, limit } = params;
  const allPages = options?.allPages === true;
  const hasRequiredPermission = useHasPermission(options?.requiredPermission ?? '');
  const canQuery = !options?.requiredPermission || hasRequiredPermission;

  const query = useApiQuery<MembershipPlanListResponse | MembershipPlan[]>({
    queryKey: membershipPlanKeys.list({
      search: search || undefined,
      status: status || undefined,
      page,
      limit,
      allPages,
    }),
    enabled: canQuery && (options?.enabled ?? true),
    showErrorToast: options?.showErrorToast,
    queryFn: async ({ signal }) => {
      const pageSize = limit ?? 100;
      const firstPage = page ?? 1;
      const fetchPage = (pageNumber?: number) =>
        apiGet<MembershipPlanListResponse | MembershipPlan[]>(
          operationsClient,
          operationsPricebookEndpoints.membershipPlans(),
          {
            signal,
            params: allPages
              ? {
                  ...(search ? { search } : {}),
                  ...(status ? { status } : {}),
                  page: pageNumber ?? firstPage,
                  limit: pageSize,
                }
              : search || status || page || limit
                ? {
                    ...(search ? { search } : {}),
                    ...(status ? { status } : {}),
                    ...(page ? { page } : {}),
                    ...(limit ? { limit } : {}),
                  }
                : undefined,
          },
        );

      const first = await fetchPage(allPages ? firstPage : undefined);
      if (!allPages || Array.isArray(first)) return first;

      const items = [...membershipPlansFromQueryData(first)];
      const total = Number(first.total ?? items.length);
      let nextPage = firstPage + 1;
      while (items.length < total) {
        const response = await fetchPage(nextPage);
        const nextItems = membershipPlansFromQueryData(response);
        if (nextItems.length === 0) break;
        items.push(...nextItems);
        nextPage += 1;
      }
      return { items, total, page: firstPage, limit: pageSize };
    },
  });
  return canQuery ? query : { ...query, data: undefined };
}

export function useMembershipPlan(id: string | undefined, options?: UseMembershipPlansOptions) {
  return useApiQuery<MembershipPlan | { data: MembershipPlan }>({
    queryKey: membershipPlanKeys.detail(id ?? '__missing__'),
    enabled: (options?.enabled ?? true) && !!id,
    showErrorToast: options?.showErrorToast,
    queryFn: ({ signal }) =>
      apiGet<MembershipPlan | { data: MembershipPlan }>(
        operationsClient,
        operationsPricebookEndpoints.membershipPlan(id!),
        { signal },
      ),
  });
}

export function useMembershipPlansByIds(
  ids: readonly string[],
  options?: UseMembershipPlansOptions,
): {
  plansById: Map<string, MembershipPlan>;
  isSettled: boolean;
  hasError: boolean;
  retry: () => void;
} {
  const hasRequiredPermission = useHasPermission(options?.requiredPermission ?? '');
  const canQuery = !options?.requiredPermission || hasRequiredPermission;
  const uniqueIds = [...new Set(ids.filter(Boolean))].sort();
  const enabled = canQuery && (options?.enabled ?? true);
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: membershipPlanKeys.pricingContext(id),
      enabled,
      retry: 1,
      retryDelay: 0,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiPost<{ data: { plans: MembershipPlan[] } }>(
          operationsClient,
          operationsPricebookEndpoints.membershipPricingContext(),
          { planIds: [id] },
          { signal },
        ).then((response) => response.data.plans[0]!),
    })),
  });
  const plansById = new Map<string, MembershipPlan>();
  uniqueIds.forEach((id, index) => {
    const plan = membershipPlanFromQueryData(queries[index]?.data);
    if (plan) plansById.set(id, plan);
  });
  const retry = useCallback(() => {
    void Promise.all(queries.filter((query) => query.isError).map((query) => query.refetch()));
  }, [queries]);
  return {
    plansById,
    isSettled: !enabled || queries.every((query) => query.isSuccess),
    hasError: enabled && queries.some((query) => query.isError),
    retry,
  };
}

/** Full authored plans for flows that must recalculate billing/coverage. */
export function useMembershipPlanDetailsByIds(
  ids: readonly string[],
  options?: UseMembershipPlansOptions,
): {
  plansById: Map<string, MembershipPlan>;
  statusById: Map<string, 'ready' | 'loading' | 'error'>;
  isSettled: boolean;
  hasError: boolean;
  retry: () => void;
} {
  const hasRequiredPermission = useHasPermission(options?.requiredPermission ?? '');
  const canQuery = !options?.requiredPermission || hasRequiredPermission;
  const uniqueIds = [...new Set(ids.filter(Boolean))].sort();
  const enabled = canQuery && (options?.enabled ?? true);
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: membershipPlanKeys.detail(id),
      enabled,
      retry: 1,
      retryDelay: 0,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiGet<MembershipPlan | { data: MembershipPlan }>(
          operationsClient,
          operationsPricebookEndpoints.membershipPlan(id),
          { signal },
        ),
    })),
  });
  const plansById = new Map<string, MembershipPlan>();
  const statusById = new Map<string, 'ready' | 'loading' | 'error'>();
  uniqueIds.forEach((id, index) => {
    const query = queries[index];
    const plan = membershipPlanFromQueryData(query?.data);
    if (plan) plansById.set(id, plan);
    statusById.set(
      id,
      query?.isError || (query?.isSuccess && !plan)
        ? 'error'
        : query?.isSuccess
          ? 'ready'
          : 'loading',
    );
  });
  const retry = useCallback(() => {
    void Promise.all(queries.filter((query) => query.isError).map((query) => query.refetch()));
  }, [queries]);
  return {
    plansById,
    statusById,
    isSettled: !enabled || queries.every((query) => query.isSuccess),
    hasError: enabled && queries.some((query) => query.isError),
    retry,
  };
}

export function membershipPlansFromQueryData(
  data: MembershipPlanListResponse | MembershipPlan[] | undefined,
): MembershipPlan[] {
  return Array.isArray(data)
    ? data
    : ((data as MembershipPlanListResponse | undefined)?.items ??
        (data as { data?: MembershipPlan[] } | undefined)?.data ??
        []);
}

export function membershipPlanFromQueryData(
  data: MembershipPlan | { data: MembershipPlan } | undefined,
): MembershipPlan | undefined {
  if (!data) return undefined;
  return 'data' in data ? data.data : data;
}
